import ScheduleType from "./ScheduleType";
import type { AgendaConsultant, AgendaGridSlot } from "./agendaTypes";
import { findHourlyRainForSlot } from "./agendaWeatherHour";
import { WEATHER_ALERT_RAIN_THRESHOLD } from "./weatherConditionUi";
import type { CityWeather } from "./agendaWeatherTypes";
import { normalizeWeatherCityKey } from "./agendaWeatherCityLookup";
import { normalizeWeatherHourKey } from "./agendaWeatherHourKey";

function isSchedulePast(scheduleDate: string): boolean {
  const normalized = scheduleDate.replace("T", " ");
  const [datePart, timePart = "00:00"] = normalized.split(" ");
  const scheduleMinute = Number(timePart.slice(0, 2)) * 60 + Number(timePart.slice(3, 5));
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart) || !Number.isFinite(scheduleMinute)) {
    return false;
  }

  const nowParts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Fortaleza",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());
  const values = Object.fromEntries(nowParts.map((part) => [part.type, part.value]));
  const today = `${values.year}-${values.month}-${values.day}`;
  if (datePart !== today) {
    return datePart < today;
  }
  return scheduleMinute <= Number(values.hour) * 60 + Number(values.minute);
}

export function buildVisitWeatherHint(params: {
  scheduleParam: string;
  targetScheduleId?: number;
  schedules: AgendaGridSlot[];
  consultants: AgendaConsultant[];
  hourRainByCity: Map<string, Map<string, number>>;
  weatherByCity?: Map<string, CityWeather>;
  weatherCityName?: string | null;
}): string | null {
  const {
    scheduleParam,
    targetScheduleId,
    schedules,
    consultants,
    hourRainByCity,
    weatherByCity,
    weatherCityName,
  } = params;

  const scheduleRow =
    schedules.find((row) => row.id === targetScheduleId) ??
    schedules.find((row) => scheduleParam.includes(String(row.id)));

  if (!scheduleRow || scheduleRow.status !== ScheduleType.SCHEDULED) {
    return null;
  }

  if (isSchedulePast(scheduleRow.date)) {
    return null;
  }

  if (scheduleRow.video_call === 1) {
    return null;
  }

  const hourPart = scheduleRow.date.replace("T", " ").split(" ")[1] ?? "";
  const hourKey = hourPart.slice(0, 5);

  const resolvedCity =
    consultants.find((entry) => entry.id === scheduleRow.consultant_id)?.city?.trim() ||
    weatherCityName?.trim();

  if (!resolvedCity) {
    return null;
  }

  const rainChance = findHourlyRainForSlot(
    hourRainByCity,
    resolvedCity,
    hourKey
  );

  const weather =
    weatherByCity?.get(resolvedCity) ??
    weatherByCity?.get(normalizeWeatherCityKey(resolvedCity));
  const hourly = weather?.hours.find(
    (entry) => normalizeWeatherHourKey(entry.time) === hourKey
  );

  if (hourly) {
    const condition = hourly.conditionLabel || "Condição não informada";
    return `${weather?.cityName ?? resolvedCity} · ${condition} · ${Math.round(
      hourly.temperatureC
    )}°C · ${hourly.rainChance}% de chuva às ${hourKey.slice(0, 2)}h`;
  }

  if (rainChance == null || rainChance < WEATHER_ALERT_RAIN_THRESHOLD) {
    return null;
  }

  return `Chuva ${rainChance}% no horário · confirme ou ofereça vídeo`;
}
